import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiMutation } from '../useApiMutation';
import { reminderKeys } from '@/lib/queryKeys';
import { remindersV2 } from '@/lib/api/endpoints';
import { apiPatch, operationsClient } from '@/lib/api';
import type { EditReminderStepDto, ReminderStep } from '@/types/reminder';

interface EditStepVariables {
  reminderId: string;
  stepId: string;
  dto: EditReminderStepDto;
}

interface EditStepResponse {
  data: ReminderStep;
}

/**
 * Instance-level edit of a scheduled step (spec §13.3 / §13.6 / §13.7).
 *
 * PATCH /reminders/:id/steps/:stepId — does not modify the underlying
 * ReminderPolicy. Backend enforces "SENDING state cannot be edited".
 */
export function useEditReminderStep() {
  const queryClient = useQueryClient();

  return useApiMutation<EditStepResponse, EditStepVariables>({
    mutationFn: ({ reminderId, stepId, dto }) =>
      apiPatch<EditStepResponse, EditReminderStepDto>(
        operationsClient,
        remindersV2.editStep(reminderId, stepId),
        dto,
      ),
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({ queryKey: reminderKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: reminderKeys.detail(variables.reminderId) });
      void queryClient.invalidateQueries({
        queryKey: reminderKeys.previewNextStep(variables.reminderId),
      });
      void queryClient.invalidateQueries({ queryKey: reminderKeys.auditLog(variables.reminderId) });
      toast.success('Step updated');
    },
  });
}
